/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, Box, debounce, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import GooglePlaceAPI from "../../api/integrations/googlePlaces";
import { Address } from "../../model/Address";

interface AddressAutocompleteProps {
  onSubmit?: (address: Address | undefined) => void;
  onInsufficient?: () => void;
  label?: string;
  required?: boolean;
  strict?: boolean;
  variant?: "standard" | "filled" | "outlined";
  value?: Address;
  size?: "small" | "medium";
}

type StructuredPlace = {
  id: string;
  first_line: string;
  suggestion: google.maps.places.AutocompleteSuggestion;
};

const ADDRESS_TO_STRING = (address?: Address) => {
  if (!address) return "";

  return `${address?.first_line}, ${address?.second_line && address?.second_line != "" ? address.second_line + ", " : ""}${address?.city}, ${address?.state} ${address?.zip_code}`;
};

export const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  onSubmit,
  label,
  required,
  strict,
  variant,
  value,
  size,
  ...props
}) => {
  const EMPTY_SUGGESTION = {
    id: "",
    first_line: value?.first_line || "",
    suggestion: new google.maps.places.AutocompleteSuggestion(),
  };
  const [suggestions, setSuggestions] = useState<Array<StructuredPlace>>([]);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<StructuredPlace>(EMPTY_SUGGESTION);
  const [input, setInput] = useState<string>(value?.first_line || "");
  const [types, setTypes] = useState<Array<string>>(["geocode"]);
  const [addrString, setAddrString] = useState<string>(
    value?.first_line ? ADDRESS_TO_STRING(value) : "",
  );
  const [color, setColor] = useState<"primary" | "error" | "success">(
    "primary",
  );

  useEffect(() => {
    if (
      value &&
      value.first_line != "" &&
      value.city != "" &&
      value.state != "" &&
      value.zip_code != 0
    ) {
      setInput(ADDRESS_TO_STRING(value));
      setAddrString(ADDRESS_TO_STRING(value));
    }
  }, [value]);

  // Determines if the autocomplete takes business names as valid input
  useEffect(() => {
    if (strict) {
      setTypes(["geocode"]);
    } else {
      setTypes(["geocode", "establishment"]);
    }
  }, [strict]);

  const handleSubmit = (value: StructuredPlace) => {
    if (onSubmit)
      GooglePlaceAPI.getSuggestionAsAddress(value?.suggestion).then((res) => {
        if (res) onSubmit(res);
      });
  };

  const fetch = async () => {
    try {
      const results = await GooglePlaceAPI.getAutocompleteSuggestionFromInput(
        input,
        types,
      );
      const arr: Array<StructuredPlace> = [];
      results.forEach((result) => {
        if (
          result.placePrediction &&
          result.placePrediction.mainText &&
          result.placePrediction.secondaryText
        ) {
          arr.push({
            id: result.placePrediction.placeId,
            first_line: `${result.placePrediction.mainText.text}, ${result.placePrediction.secondaryText.text}`,
            suggestion: result,
          });
        }
      });

      setSuggestions(arr);
    } catch (error) {
      console.error("Error fetching autocomplete suggestions:", error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useMemo(() => debounce(fetch, 200), [input]);
  useEffect(() => {
    if (
      input != addrString &&
      addrString != "" &&
      selectedSuggestion != EMPTY_SUGGESTION
    ) {
      // Autocomplete input does not match user input, clear address
      setSelectedSuggestion(EMPTY_SUGGESTION);
      setColor("error");
      if (onSubmit) onSubmit(undefined);
    } else if (input == "" || input == undefined) {
      // No input, set color to neutral
      setColor("primary");
      setAddrString("");
      if (onSubmit) onSubmit(undefined);
    } else if (addrString == input) {
      setColor("primary");
    }

    if (input.length > 3) {
      debouncedFetch();
    } else {
      setSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  return (
    <Autocomplete
      freeSolo
      inputValue={input}
      value={selectedSuggestion}
      autoHighlight
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          color={color}
          size={size}
          label={label}
          variant={variant || "outlined"}
        />
      )}
      options={suggestions}
      renderOption={(props, option) => {
        return (
          <Box {...props} key={option.id} component="li">
            {option.first_line}
          </Box>
        );
      }}
      onInputChange={(_, newValue, reason) => {
        if (reason == "clear") {
          setInput("");
          setSelectedSuggestion(EMPTY_SUGGESTION);
          if (onSubmit) onSubmit(undefined);
        } else if (reason == "input" && newValue != "") {
          setInput(newValue);
        } else if (reason == "input" && newValue == "") {
          setInput("");
          setSelectedSuggestion(EMPTY_SUGGESTION);
          if (onSubmit) onSubmit(undefined);
        }
      }}
      onChange={(_, newValue) => {
        if (newValue && typeof newValue != "string") {
          setSelectedSuggestion(newValue);
          setInput(newValue.first_line);
          GooglePlaceAPI.getSuggestionAsAddress(newValue?.suggestion).then(
            (res) => {
              if (!res) {
                props.onInsufficient?.();
                return;
              }

              const addressString = ADDRESS_TO_STRING(res);

              setInput(addressString);
              handleSubmit(newValue);
              setAddrString(addressString);
            },
          );
        }
      }}
      getOptionLabel={(option) => {
        if (typeof option == "string") return "";
        return option.first_line;
      }}
      isOptionEqualToValue={(option, value) => {
        return option.id === value.id && option.first_line === value.first_line;
      }}
    ></Autocomplete>
  );
};
